import * as React from "react";
import { Textarea as BaseTextArea } from "react-ui";

import { useScrollTo } from "../../Internals/Contexts/ScrollToContext";
import { MetricsValueSenderActions } from "../../Internals/Metrics/MetricsValueSender";
import { Tooltip } from "../../Internals/Tooltip/Tooltip";
import { emptyRef } from "../../TypingUtils";
import { FocusManagementObserver } from "../../Services/FocusManagement/FocusManagementObserver";
import { useTestPageObject } from "../../Services/TestPageObjects/PageObjectExposure";
import { ControlValidationProps } from "../../Validations/ValidationProps";
import { ControlTooltipProps } from "../../Internals/Tooltip/ControlTooltipProps";
import { AbsoluteModelPath } from "../../../../../Common/ModelPath/ModelPath";
import { Text } from "../../ValueViewers/Text/Text";
import { cutExtraSpacesFromPaste, cutExtraSpaces } from "../../../Helpers/CutExtraSpaces";

import { CurrentLength, Root, TextLengthInfo } from "./TextArea.styles";
import { TextAreaTestPageObject } from "./TextAreaTestPageObject";

export interface TextAreaProps extends ControlValidationProps, ControlTooltipProps {
    bindingPath: AbsoluteModelPath;
    value: undefined | string;
    onChange: (nextValue: string) => void;
    width?: string | number;
    placeholder?: string;
    disabled?: boolean;
    isTooltipEnabled?: boolean;
    tooltip?: string;
    rows?: number;
    resize?: React.CSSProperties["resize"];
    autoResize?: boolean;
    metrics: MetricsValueSenderActions;
    textMaxLength?: number;
    isReadOnly?: boolean;
    isLink?: boolean;
    onClick?: () => void;
    "data-tid"?: string;
}

export function TextArea(props: TextAreaProps): JSX.Element {
    const container = React.useRef<HTMLDivElement>(emptyRef);
    const textarea = React.useRef<BaseTextArea>(emptyRef);
    const scrollTo = useScrollTo();

    useTestPageObject(
        () => TextAreaTestPageObject(props.value, textarea, container, props.onChange),
        [props.value, textarea, container]
    );

    const handleRequestFocus = () => {
        if (textarea.current != undefined) {
            if (container.current != undefined) {
                scrollTo(container.current);
            }
            textarea.current.focus();
            textarea.current.selectAll();
        }
    };

    const { error, tooltip, warning, isTooltipEnabled } = props;
    const ownTooltipContent = error || warning || tooltip;
    const tooltipTrigger =
        ownTooltipContent != undefined && ownTooltipContent !== "" && isTooltipEnabled ? "hover&focus" : "closed";
    const tooltipPosition = props.tooltipPosition ?? "top left";

    const handleFocus = () => props.metrics.saveValue(props.value);

    const handleBlur = () => {
        const formattedText = cutExtraSpaces(props.value);
        props.onChange(formattedText);
        props.metrics.sendValue(props.bindingPath, formattedText);
    };

    const renderTextLengthInfo = () => {
        if (props.textMaxLength == undefined) {
            return undefined;
        }
        return (
            <TextLengthInfo>
                <CurrentLength error={(props.value?.length ?? 0) > props.textMaxLength}>
                    {props.value?.length ?? 0}
                </CurrentLength>
                {" / "}
                {props.textMaxLength.toString()}
            </TextLengthInfo>
        );
    };

    const handlePaste = (event: React.ClipboardEvent<unknown>) => {
        event.preventDefault();
        const formattedText = cutExtraSpacesFromPaste({ event: event, previousText: props.value });
        props.onChange(formattedText);
    };

    return props.isReadOnly ? (
        <div style={{ marginTop: "8px" }}>
            <Text
                bindingPath={props.bindingPath}
                value={props.value}
                isLink={props.isLink}
                onClick={props.onClick}
                data-tid={props["data-tid"]}
            />
        </div>
    ) : (
        <FocusManagementObserver onRequestFocus={handleRequestFocus}>
            <Root width={props.width} ref={container}>
                <Tooltip
                    trigger={tooltipTrigger}
                    value={ownTooltipContent}
                    useWrapper={false}
                    pos={tooltipPosition}
                    width={props.tooltipWidth}>
                    <BaseTextArea
                        ref={textarea}
                        width={props.width}
                        data-tid={props["data-tid"]}
                        placeholder={props.placeholder}
                        disabled={props.disabled}
                        error={Boolean(error)}
                        value={props.value}
                        rows={props.rows}
                        resize={props.resize}
                        autoResize={props.autoResize}
                        warning={Boolean(warning)}
                        onValueChange={props.onChange}
                        onPaste={handlePaste}
                        onFocus={handleFocus}
                        onBlur={handleBlur}
                    />
                </Tooltip>
                {renderTextLengthInfo()}
            </Root>
        </FocusManagementObserver>
    );
}
